/**
 * What a court record is missing, so a picker can show a blank court as blank (27 Oct 2026).
 *
 * The OCA import (#139) created courts with a name and county and nothing else. In the picker they
 * read "Fort Bend County Court at Law No. 2 - " next to our complete record of the same court, and
 * staff picked the blank one. A letter cannot be emailed to a court with no email on file.
 */

export interface CourtContactFields {
  Court_Name: string;
  Court_Email?: string | null;
  Court_Address?: string | null;
}

export type CourtContactGap = 'email' | 'address';

const filled = (value: string | null | undefined) => !!value && value.trim().length > 0;

export function courtContactGaps(court: CourtContactFields): CourtContactGap[] {
  const gaps: CourtContactGap[] = [];
  if (!filled(court.Court_Email)) gaps.push('email');
  if (!filled(court.Court_Address)) gaps.push('address');
  return gaps;
}

/** Neither an email nor an address: nothing to file to. */
export function isBlankCourt(court: CourtContactFields): boolean {
  return courtContactGaps(court).length === 2;
}

/** The text in the field once a court is chosen: the name, and the address only when there is one. */
export function courtOptionLabel(court: CourtContactFields): string {
  return filled(court.Court_Address) ? `${court.Court_Name} - ${court.Court_Address!.trim()}` : court.Court_Name;
}

/** The lines under a court in the list: its email and address, or what is missing. */
export function courtContactLines(court: CourtContactFields): Array<{ text: string; missing: boolean }> {
  return [
    filled(court.Court_Email) ? { text: court.Court_Email!.trim(), missing: false } : { text: 'No email on file', missing: true },
    filled(court.Court_Address) ? { text: court.Court_Address!.trim(), missing: false } : { text: 'No address on file', missing: true },
  ];
}

/** Said under the field when the chosen court has no email, or null when it has one. */
export function courtNoEmailWarning(court: CourtContactFields | null | undefined): string | null {
  if (!court || filled(court.Court_Email)) return null;
  return 'This court has no email on file, so letters cannot be emailed to it until one is added.';
}
